import { createReducer } from '@reduxjs/toolkit'

import {
  setVirtualBackground,
  removeVirtualBackground,
  blurVirtualBackground,
  clearToastStates,
  getCurrentBackground,
  setCurrentImage,
  addPreviousImage,
  deletePreviousImage,
  setIsVbSettingImage,
  generateTextToImages,
  setTextToImages,
  setTextToImageError,
  resetTextToImage,
  addVirtualBackground,
  resetAddImage,
} from './actions'
import { VB_IMAGE_TYPES } from './constants'
import {
  ERROR_MESSAGE_FAILED_TO_GENERATE,
  ERROR_MESSAGE_RATE_LIMIT,
} from '../clientSetting/constants'

const initialState = {
  error: null,
  uploading: false,
  successfullyUploaded: false,
  removing: false,
  image: null,
  previousImages: [],
  pendingImage: null,
  isVbSettingImage: false,
  addImageLoading: false,
  addImageSuccess: false,
  addImageError: null,
  textToImages: [],
  textToImagesLoading: false,
  textToImagesSuccess: false,
  textToImagesError: null,
  textToImagesRequestId: null,
  activeTextToImagesThunkId: null,
}

const MAX_PREVIOUS_IMAGES = 20

export default createReducer(initialState, (builder) => {
  builder
    .addCase(setVirtualBackground.pending, (state, action) => {
      state.pendingImage = action.meta.arg.image
      state.uploading = true
      state.successfullyUploaded = false
      state.error = null
      state.isVbSettingImage = true
    })
    .addCase(setVirtualBackground.fulfilled, (state, action) => {
      state.image = action.meta.arg.image
      state.pendingImage = null
      state.successfullyUploaded = true
      state.uploading = false
    })
    .addCase(setVirtualBackground.rejected, (state, action) => {
      state.pendingImage = null
      state.uploading = false
      state.error = action.error
      state.previousImages.pop()
      state.isVbSettingImage = false
    })
    .addCase(addVirtualBackground.pending, (state, action) => {
      state.addImageLoading = true
    })
    .addCase(addVirtualBackground.fulfilled, (state, action) => {
      state.addImageLoading = false
      state.addImageSuccess = true
    })
    .addCase(addVirtualBackground.rejected, (state, action) => {
      state.addImageLoading = false
      state.addImageSuccess = false
      state.addImageError = action.payload
    })
    .addCase(removeVirtualBackground.pending, (state) => {
      state.successfullyUploaded = false
      state.removing = true
      state.error = null
      state.isVbSettingImage = true
    })
    .addCase(removeVirtualBackground.fulfilled, (state) => {
      state.removing = false
      state.image = { type: VB_IMAGE_TYPES.none }
    })
    .addCase(removeVirtualBackground.rejected, (state, action) => {
      state.removing = false
      state.error = action.error
      state.previousImages.pop()
      state.isVbSettingImage = false
    })
    .addCase(blurVirtualBackground.pending, (state) => {
      state.successfullyUploaded = false
      state.removing = true
      state.error = null
      state.isVbSettingImage = true
    })
    .addCase(blurVirtualBackground.fulfilled, (state) => {
      state.removing = false
      state.image = { type: VB_IMAGE_TYPES.blur }
    })
    .addCase(blurVirtualBackground.rejected, (state, action) => {
      state.removing = false
      state.error = action.error
      state.previousImages.pop()
      state.isVbSettingImage = false
    })
    .addCase(clearToastStates, (state) => {
      state.successfullyUploaded = false
      state.error = null
    })
    .addCase(getCurrentBackground.fulfilled, (state, action) => {
      state.image = action.payload
    })
    .addCase(setCurrentImage.fulfilled, (state, action) => {
      state.image = action.payload
    })
    .addCase(addPreviousImage, (state, action) => {
      if (!action.payload) return
      const previousImage =
        state.previousImages[state.previousImages.length - 1]

      // In case both the previous and current backgrounds are "blur" or "none," we aim to avoid duplication.
      if (
        !previousImage ||
        (previousImage.type !== VB_IMAGE_TYPES.blur &&
          previousImage.type !== VB_IMAGE_TYPES.none) ||
        action.payload.type !== previousImage?.type
      ) {
        state.previousImages.push(action.payload)
        if (state.previousImages.length > MAX_PREVIOUS_IMAGES) {
          state.previousImages.shift()
        }
      }
    })
    .addCase(deletePreviousImage, (state) => {
      /**
       * This is for "undo" and "previous" feature that, we have to pop the history stack twice
       * We delete the current image and the most recent history
       */
      state.previousImages.pop()
      state.previousImages.pop()
    })
    .addCase(setIsVbSettingImage, (state, action) => {
      state.isVbSettingImage = action.payload
    })
    .addCase(generateTextToImages.pending, (state, action) => {
      state.textToImagesLoading = true
      state.textToImagesSuccess = false
      state.textToImagesError = null
      state.textToImages = []
      state.textToImagesRequestId = null
      state.activeTextToImagesThunkId = action.meta.requestId
    })
    .addCase(generateTextToImages.fulfilled, (state, action) => {
      if (state.activeTextToImagesThunkId !== action.meta.requestId) {
        return
      }

      // do not set Loading state to false because we will set it after onTextToImage event trigger
      state.textToImagesSuccess = true
      state.textToImagesError = null
      state.textToImagesRequestId = action.payload?.requestId || null
    })
    .addCase(generateTextToImages.rejected, (state, action) => {
      if (state.activeTextToImagesThunkId !== action.meta.requestId) {
        return
      }

      state.textToImagesLoading = false
      state.textToImagesSuccess = false
      state.activeTextToImagesThunkId = null

      const { error } = action

      if (typeof error === 'object') {
        state.textToImagesError =
          error.message.includes('300') || error.message.includes('301')
            ? ERROR_MESSAGE_RATE_LIMIT
            : ERROR_MESSAGE_FAILED_TO_GENERATE
      } else {
        state.textToImagesError =
          typeof error === 'string' ? error : ERROR_MESSAGE_FAILED_TO_GENERATE
      }
    })
    .addCase(setTextToImages.fulfilled, (state, action) => {
      state.textToImages = action.payload
      state.textToImagesError = null
      state.textToImagesLoading = false
      state.textToImagesSuccess = true
      state.activeTextToImagesThunkId = null
    })
    .addCase(setTextToImages.rejected, (state, action) => {
      state.textToImages = []
      state.textToImagesError = action.error
      state.textToImagesLoading = false
      state.textToImagesSuccess = false
      state.activeTextToImagesThunkId = null
    })
    .addCase(setTextToImageError, (state, action) => {
      state.textToImagesError = action.payload
      state.textToImagesSuccess = false
      state.textToImagesLoading = false
      state.textToImages = []
      state.textToImagesRequestId = null
      state.activeTextToImagesThunkId = null
    })
    .addCase(resetTextToImage, (state) => {
      state.textToImages = []
      state.textToImagesLoading = false
      state.textToImagesSuccess = false
      state.textToImagesError = null
      state.textToImagesRequestId = null
      state.activeTextToImagesThunkId = null
    })
    .addCase(resetAddImage, (state) => {
      state.addImageLoading = false
      state.addImageSuccess = false
      state.addImageError = null
    })
    .addDefaultCase((state, action) => {})
})

export function selectIsVbUploading(state) {
  return state.vb.uploading
}

export function selectIsSuccessfullyUploaded(state) {
  return state.vb.successfullyUploaded
}

export function selectIsVbRemoving(state) {
  return state.vb.removing
}

export function selectVbImage(state) {
  return state.vb.image
}

export function selectPreviousImage(state) {
  return state.vb.previousImages[state.vb.previousImages.length - 1]
}

export function selectAllPreviousImages(state) {
  return state.vb.previousImages
}

export function selectPendingVbImage(state) {
  return state.vb.pendingImage
}

export function selectVbError(state) {
  return state.vb.error
}

export function selectIsVbSettingImage(state) {
  return state.vb.isVbSettingImage
}

export function selectTextToImages(state) {
  return state.vb.textToImages
}

export function selectTextToImagesSuccess(state) {
  return state.vb.textToImagesSuccess
}

export function selectTextToImagesLoading(state) {
  return state.vb.textToImagesLoading
}

export function selectTextToImagesError(state) {
  return state.vb.textToImagesError
}

export function selectTextToImagesRequestId(state) {
  return state.vb.textToImagesRequestId
}

export function selectAddImageLoading(state) {
  return state.vb.addImageLoading
}

export function selectAddImageSuccess(state) {
  return state.vb.addImageSuccess
}

export function selectAddImageError(state) {
  return state.vb.addImageError
}
